import React, { useState, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Printer, TrendingUp } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import api from '../../../services/api';
import Button from '../../../components/ui/Button';
import { formatDate, formatDateTime } from '../../../utils/Utils';

// P1-C — çalışan detay "Performans" sekmesi (PM.Read; sekme kaydı
// EmployeeDetailsPage'de canRead('PM') ile kapılı). Veri: GET
// v1/feedback-responses/summary/history?employeeId= — yönetici/İK trend serisi
// (FinalLevel DAHİL; BE kapsam kontrolü: admin değilse yalnız doğrudan rapor —
// kapsam dışıysa 403 → boş durum). Grafik/tema DistributionChartCard tema-token
// deseni (hardcode renk YASAK); satır başına karne bağlantısı (yönetici
// varyantı: /performance/report-card/:periodId/:employeeId).
const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 18px 20px;
  margin-bottom: 16px;
`;

const CardTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
`;

const EmptyIconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  max-width: 420px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    text-align: left;
    padding: 8px 10px;
    font-size: ${({ theme }) => theme.metrics.font.size.sm};
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  th {
    color: ${({ theme }) => theme.colors.text.dark.little};
    font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  }

  td {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const LevelBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.primary.main};
`;

export default function EmployeePerformanceTab({ employeeId }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!employeeId) return undefined;
    let cancelled = false;
    setFetching(true);
    api.get('/v1/feedback-responses/summary/history', { params: { employeeId }, skipErrorToast: true })
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.data;
        setRows(Array.isArray(items) ? items : []);
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [employeeId]);

  const chartData = useMemo(() => rows.map((r) => ({
    name: r.periodName,
    systemAScore: r.systemAScore != null ? Number(r.systemAScore) : null,
    weightedOverall: r.weightedOverall != null ? Number(r.weightedOverall) : null,
  })), [rows]);

  const hasA = chartData.some((d) => d.systemAScore != null);
  const hasB = chartData.some((d) => d.weightedOverall != null);

  if (fetching) {
    return <Card><Centered><EmptyText>{t('common.loading')}</EmptyText></Centered></Card>;
  }

  if (rows.length === 0) {
    return (
      <Card>
        <Centered>
          <EmptyIconWrap><TrendingUp size={26} /></EmptyIconWrap>
          <EmptyText>{t('employee.performanceHistoryEmpty')}</EmptyText>
        </Centered>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardTitle>{t('employee.performanceTrend')}</CardTitle>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.surface.border} vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: theme.colors.text.muted }}
              axisLine={{ stroke: theme.colors.surface.border }}
              tickLine={false}
            />
            <YAxis yAxisId="a" hide domain={[0, 100]} />
            <YAxis yAxisId="b" hide domain={[0, 5]} orientation="right" />
            <Tooltip cursor={{ stroke: theme.colors.surface.border }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {hasA && (
              <Line
                yAxisId="a"
                type="monotone"
                dataKey="systemAScore"
                name={t('performance.results.trendSystemA')}
                stroke={theme.colors.primary.main}
                strokeWidth={2}
                dot={{ r: 3, fill: theme.colors.primary.main }}
                connectNulls
              />
            )}
            {hasB && (
              <Line
                yAxisId="b"
                type="monotone"
                dataKey="weightedOverall"
                name={t('performance.results.trendSystemB')}
                stroke={theme.colors.auxiliar.purple}
                strokeWidth={2}
                dot={{ r: 3, fill: theme.colors.auxiliar.purple }}
                connectNulls
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <CardTitle>{t('employee.performancePeriods')}</CardTitle>
        <StyledTable>
          <thead>
            <tr>
              <th>{t('performance.period.name')}</th>
              <th>{t('performance.results.closedAt')}</th>
              <th>{t('performance.results.trendSystemA')}</th>
              <th>{t('performance.results.trendSystemB')}</th>
              <th>{t('performance.results.finalLevel')}</th>
              <th>{t('performance.results.acknowledgedColumn')}</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.periodId}>
                <td>{r.periodName}</td>
                <td>{r.closedAt ? formatDate(r.closedAt, i18n.language) : '-'}</td>
                <td>{r.systemAScore ?? '-'}</td>
                <td>{r.weightedOverall ?? '-'}</td>
                <td>
                  {r.finalLevelLabel ? (
                    <LevelBadge>{r.finalLevelLabel}</LevelBadge>
                  ) : '-'}
                </td>
                <td>{r.acknowledgedAt ? formatDateTime(r.acknowledgedAt, i18n.language) : '-'}</td>
                <td>
                  <Button
                    variant="ghost"
                    onClick={() => history.push(`/performance/report-card/${r.periodId}/${employeeId}`)}
                  >
                    <Printer size={14} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                    {t('performance.reportCard.open')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </Card>
    </>
  );
}
